// widget/src/components/CuratedEmojiPicker.tsx
//
// Tiny curated emoji grid for the Pearl widget composer. NO library — the
// full emoji-picker-react is ~250KB and would nearly double the widget
// bundle. This curated list of ~120 most-common emojis covers the 95% of
// customer use cases (smileys, hand gestures, hearts, cars, paint-related
// stuff) at a cost of ~2KB.
//
// If the customer needs an emoji not in the curated list, they can use
// the OS native keyboard picker (Cmd+Ctrl+Space on Mac, Win+. on Windows,
// the emoji button on mobile keyboards). The textarea stays focused so
// either input path works.

import { useState } from "react"

interface Props {
  onPick: (emoji: string) => void
}

// Organized by category. Order = popularity (roughly). Categories help
// the customer scan; the inline labels are short and lowercase to match
// the widget's casual tone.
const CATEGORIES = [
  {
    label: "Smileys",
    emojis: ["😀", "😃", "😄", "😁", "😆", "😅", "🤣", "😂", "🙂", "🙃", "😉", "😊", "😇", "🥰", "😍", "🤩", "😘", "😗", "😚", "😋", "😛", "😜", "🤪", "😝", "🤑", "🤗", "🤔", "🤐", "🤨", "😐", "😑", "😶", "😏", "😒", "🙄", "😬", "🤥"],
  },
  {
    label: "Reactions",
    emojis: ["👍", "👎", "👌", "✌️", "🤞", "🤟", "🤘", "🤙", "👈", "👉", "👆", "🖕", "👇", "☝️", "👏", "🙌", "👐", "🤲", "🤝", "🙏", "💪", "🦾", "✍️", "❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "🤎", "💔", "❣️", "💕", "💞", "💓", "💗", "💖", "💘", "💝", "🔥", "✨", "⭐", "🌟", "💯", "💢", "💥", "👀", "😎"],
  },
  {
    label: "Cars & paint",
    emojis: ["🚗", "🚙", "🛻", "🏎️", "🚐", "🚛", "🚜", "🛺", "🏍️", "🛵", "🚲", "🛴", "🛞", "⚙️", "🔧", "🔨", "🪛", "🛠️", "🧰", "🎨", "🖌️", "🖼️", "💧", "🌈", "⚫", "⚪", "🔴", "🟠", "🟡", "🟢", "🔵", "🟣", "🟤", "🤍", "🩶"],
  },
  {
    label: "Yes & no",
    emojis: ["✅", "☑️", "✔️", "❌", "❎", "✖️", "⭕", "🚫", "⛔", "💯", "❗", "❓", "❕", "❔", "⚠️", "🆗", "🆙", "🆕"],
  },
] as const

export function CuratedEmojiPicker({ onPick }: Props) {
  // Track which category is expanded. Default to Smileys.
  const [active, setActive] = useState(0)
  const cat = CATEGORIES[active]
  return (
    <div
      style={{
        width: 280,
        background: "rgba(20,20,24,0.97)",
        backdropFilter: "blur(16px)",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 12,
        padding: 10,
        boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 4,
          marginBottom: 8,
          paddingBottom: 8,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {CATEGORIES.map((c, i) => (
          <button
            key={c.label}
            type="button"
            onClick={() => setActive(i)}
            style={{
              flex: 1,
              padding: "5px 6px",
              fontSize: 10,
              fontFamily: "inherit",
              color: i === active ? "#fff" : "rgba(255,255,255,0.55)",
              background:
                i === active ? "rgba(255,255,255,0.1)" : "transparent",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontWeight: 500,
            }}
          >
            {c.label}
          </button>
        ))}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(8, 1fr)",
          gap: 2,
          maxHeight: 200,
          overflowY: "auto",
        }}
      >
        {cat.emojis.map((e, i) => (
          <button
            key={`${active}-${i}`}
            type="button"
            onClick={() => onPick(e)}
            title={e}
            style={{
              fontSize: 20,
              padding: "6px 0",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              borderRadius: 6,
              transition: "background 0.12s",
              lineHeight: 1,
              fontFamily: "inherit",
            }}
            onMouseEnter={(ev) => {
              ;(ev.currentTarget as HTMLButtonElement).style.background =
                "rgba(255,255,255,0.1)"
            }}
            onMouseLeave={(ev) => {
              ;(ev.currentTarget as HTMLButtonElement).style.background =
                "transparent"
            }}
          >
            {e}
          </button>
        ))}
      </div>
      <div
        style={{
          marginTop: 6,
          fontSize: 10,
          color: "rgba(255,255,255,0.4)",
          textAlign: "center",
        }}
      >
        Need more? Use your keyboard&apos;s emoji picker.
      </div>
    </div>
  )
}
